import { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { PlusCircle, Pencil, Trash2 } from "lucide-react";

const EMPTY = {
  title: "", slug: "", summary: "", content: "", category: "News",
  cover_image: "", seo_title: "", seo_description: "", status: "draft",
};

const inputCls = "w-full border border-sage bg-white px-4 py-2.5 text-sm rounded-sm focus:border-forest focus:ring-1 focus:ring-forest";

export default function AdminNews() {
  const [articles, setArticles] = useState([]);
  const [editing, setEditing] = useState(null); // null | {id?} + fields
  const [state, setState] = useState({ status: "idle", message: "" });

  const load = () => api.get("/admin/articles").then(({ data }) => setArticles(data.articles)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing({ ...EMPTY }); setState({ status: "idle", message: "" }); };
  const openEdit = (a) => { setEditing({ ...EMPTY, ...a }); setState({ status: "idle", message: "" }); };

  const set = (k) => (e) => {
    const v = e.target.value;
    setEditing((cur) => ({
      ...cur, [k]: v,
      ...(k === "title" && !cur.id ? { slug: v.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "") } : {}),
    }));
  };

  const save = async (e) => {
    e.preventDefault();
    setState({ status: "loading", message: "" });
    try {
      if (editing.id) await api.put(`/admin/articles/${editing.id}`, editing);
      else await api.post("/admin/articles", editing);
      setEditing(null);
      setState({ status: "idle", message: "" });
      load();
    } catch (err) {
      setState({ status: "error", message: formatApiError(err) });
    }
  };

  const remove = async (a) => {
    if (!window.confirm(`Delete "${a.title}"? This cannot be undone.`)) return;
    await api.delete(`/admin/articles/${a.id}`).catch(() => {});
    load();
  };

  if (editing) {
    return (
      <div data-testid="article-editor">
        <h1 className="font-serif text-3xl font-semibold text-forest">{editing.id ? "Edit article" : "New article"}</h1>
        <form onSubmit={save} className="mt-6 bg-white border border-sandstone p-6 lg:p-8 space-y-5 max-w-3xl">
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <label className="block text-xs tracking-wider uppercase font-semibold text-charcoal/70 mb-2" htmlFor="a-title">Title</label>
              <input id="a-title" required value={editing.title} onChange={set("title")} className={inputCls} data-testid="article-title-input" />
            </div>
            <div>
              <label className="block text-xs tracking-wider uppercase font-semibold text-charcoal/70 mb-2" htmlFor="a-slug">Slug (URL)</label>
              <input id="a-slug" required pattern="[a-z0-9]+(-[a-z0-9]+)*" value={editing.slug} onChange={set("slug")} className={inputCls} data-testid="article-slug-input" />
            </div>
          </div>
          <div>
            <label className="block text-xs tracking-wider uppercase font-semibold text-charcoal/70 mb-2" htmlFor="a-summary">Summary</label>
            <textarea id="a-summary" rows={2} maxLength={500} value={editing.summary} onChange={set("summary")} className={inputCls} data-testid="article-summary-input" />
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <label className="block text-xs tracking-wider uppercase font-semibold text-charcoal/70 mb-2" htmlFor="a-category">Category</label>
              <select id="a-category" value={editing.category} onChange={set("category")} className={inputCls} data-testid="article-category-select">
                {["News", "Announcement", "Project Update", "Fundraising", "Volunteering", "Impact Report"].map((c) => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs tracking-wider uppercase font-semibold text-charcoal/70 mb-2" htmlFor="a-status">Status</label>
              <select id="a-status" value={editing.status} onChange={set("status")} className={inputCls} data-testid="article-status-select">
                <option value="draft">Draft (hidden from public)</option>
                <option value="published">Published (visible publicly)</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-xs tracking-wider uppercase font-semibold text-charcoal/70 mb-2" htmlFor="a-cover">Cover image URL (approved images only)</label>
            <input id="a-cover" type="url" value={editing.cover_image} onChange={set("cover_image")} className={inputCls} data-testid="article-cover-input" />
          </div>
          <div>
            <label className="block text-xs tracking-wider uppercase font-semibold text-charcoal/70 mb-2" htmlFor="a-content">Content</label>
            <textarea id="a-content" rows={12} value={editing.content} onChange={set("content")} className={inputCls} data-testid="article-content-input" />
            <p className="mt-1 text-xs text-charcoal/50">Separate paragraphs with a blank line. Handle any beneficiary story with dignity and consent.</p>
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <label className="block text-xs tracking-wider uppercase font-semibold text-charcoal/70 mb-2" htmlFor="a-seot">SEO title (optional)</label>
              <input id="a-seot" maxLength={200} value={editing.seo_title} onChange={set("seo_title")} className={inputCls} />
            </div>
            <div>
              <label className="block text-xs tracking-wider uppercase font-semibold text-charcoal/70 mb-2" htmlFor="a-seod">SEO description (optional)</label>
              <input id="a-seod" maxLength={300} value={editing.seo_description} onChange={set("seo_description")} className={inputCls} />
            </div>
          </div>
          {state.status === "error" && <p className="text-sm text-red-700" role="alert" data-testid="article-error">{state.message}</p>}
          <div className="flex gap-3">
            <button type="submit" disabled={state.status === "loading"} data-testid="article-save-button"
              className="bg-forest text-white text-sm font-medium px-6 py-2.5 hover:bg-forest-dark transition-colors duration-300 disabled:opacity-60">
              {state.status === "loading" ? "Saving…" : "Save article"}
            </button>
            <button type="button" onClick={() => setEditing(null)} data-testid="article-cancel-button"
              className="text-sm border border-sage px-6 py-2.5 hover:bg-sandstone transition-colors duration-300">Cancel</button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div data-testid="admin-news">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-serif text-3xl font-semibold text-forest">News &amp; stories</h1>
          <p className="mt-2 text-sm text-charcoal/70">Only published articles appear on the public website.</p>
        </div>
        <button onClick={openNew} data-testid="article-new-button"
          className="inline-flex items-center gap-2 bg-forest text-white text-sm font-medium px-5 py-2.5 hover:bg-forest-dark transition-colors duration-300">
          <PlusCircle size={16} aria-hidden="true" /> New article
        </button>
      </div>

      <div className="mt-6 bg-white border border-sandstone overflow-x-auto">
        <table className="w-full text-sm" data-testid="articles-table">
          <thead>
            <tr className="text-left border-b border-sandstone text-xs uppercase tracking-wider text-charcoal/60">
              <th className="px-5 py-3 font-semibold">Title</th>
              <th className="px-5 py-3 font-semibold">Category</th>
              <th className="px-5 py-3 font-semibold">Status</th>
              <th className="px-5 py-3 font-semibold">Updated</th>
              <th className="px-5 py-3 font-semibold"><span className="sr-only">Actions</span></th>
            </tr>
          </thead>
          <tbody>
            {articles.map((a) => (
              <tr key={a.id} className="border-b border-sandstone/60 last:border-0">
                <td className="px-5 py-3 font-medium text-charcoal">{a.title}</td>
                <td className="px-5 py-3">{a.category}</td>
                <td className="px-5 py-3">
                  <span className={`text-xs font-semibold uppercase tracking-wider ${a.status === "published" ? "text-forest" : "text-gold-dark"}`}>
                    {a.status}
                  </span>
                </td>
                <td className="px-5 py-3 text-charcoal/60">{new Date(a.updated_at).toLocaleDateString("en-GB")}</td>
                <td className="px-5 py-3">
                  <div className="flex gap-2 justify-end">
                    <button onClick={() => openEdit(a)} aria-label={`Edit ${a.title}`} data-testid={`article-edit-${a.slug}`} className="p-2 text-charcoal/60 hover:text-forest"><Pencil size={16} /></button>
                    <button onClick={() => remove(a)} aria-label={`Delete ${a.title}`} data-testid={`article-delete-${a.slug}`} className="p-2 text-charcoal/60 hover:text-red-700"><Trash2 size={16} /></button>
                  </div>
                </td>
              </tr>
            ))}
            {articles.length === 0 && (
              <tr><td colSpan={5} className="px-5 py-8 text-center text-charcoal/50">No articles yet. Publish only verified news.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
